// Demo/mock layer for Daily Reports. Persists to localStorage so the
// preview feels real across reloads. All demo IDs match the ones used
// inside the live Supabase seed (supabase/seed.sql) where possible.

import { ENTRY_TYPES } from "./entryTypes";

const STORE_KEY = "daycare.demo.reports.v1";
const ORG_ID = "00000000-0000-0000-0000-000000000001";

// -------------------------------------------------------------------------
// Static fixtures (children / classrooms / family wiring)
// -------------------------------------------------------------------------
export const DEMO_CLASSROOMS = [
  { id: "00000000-0000-0000-0000-000000000101", name: "Sunflower", age_group: "Toddler (2–3)" },
  { id: "00000000-0000-0000-0000-000000000102", name: "Willow",    age_group: "Preschool (3–4)" },
  { id: "00000000-0000-0000-0000-000000000103", name: "Maple",     age_group: "Pre-K (4–5)" },
  { id: "00000000-0000-0000-0000-000000000104", name: "Acorn",     age_group: "Infant (0–1)" },
];

export const DEMO_CHILDREN = [
  {
    id: "00000000-0000-0000-0000-000000000301",
    first_name: "Noa",
    last_name: "Cohen",
    classroom_id: "00000000-0000-0000-0000-000000000101",
    family_id: "00000000-0000-0000-0000-000000000201",
    parent_profile_id: "demo-parent",
    allergies: "Peanuts",
  },
  {
    id: "00000000-0000-0000-0000-000000000302",
    first_name: "Maya",
    last_name: "Patel",
    classroom_id: "00000000-0000-0000-0000-000000000101",
    family_id: "00000000-0000-0000-0000-000000000202",
    parent_profile_id: null,
  },
  {
    id: "00000000-0000-0000-0000-000000000303",
    first_name: "Ezra",
    last_name: "Miller",
    classroom_id: "00000000-0000-0000-0000-000000000101",
    family_id: "00000000-0000-0000-0000-000000000203",
    parent_profile_id: null,
  },
  {
    id: "00000000-0000-0000-0000-000000000304",
    first_name: "Aisha",
    last_name: "Ndiaye",
    classroom_id: "00000000-0000-0000-0000-000000000101",
    family_id: "00000000-0000-0000-0000-000000000204",
    parent_profile_id: null,
  },
  {
    id: "00000000-0000-0000-0000-000000000305",
    first_name: "Theo",
    last_name: "Brown",
    classroom_id: "00000000-0000-0000-0000-000000000102",
    family_id: "00000000-0000-0000-0000-000000000205",
    parent_profile_id: null,
  },
];

// Demo teacher "covers" these classrooms (used as the teacher's active roster).
export const DEMO_TEACHER_CLASSROOM_IDS = [
  "00000000-0000-0000-0000-000000000101",
];

export function getClassroomById(id) {
  return DEMO_CLASSROOMS.find((c) => c.id === id) || null;
}

export function getChildById(id) {
  return DEMO_CHILDREN.find((c) => c.id === id) || null;
}

export function getChildrenForClassroom(classroomId) {
  return DEMO_CHILDREN.filter((c) => c.classroom_id === classroomId);
}

export function getChildrenForParent(parentProfileId) {
  return DEMO_CHILDREN.filter((c) => c.parent_profile_id === parentProfileId);
}

// -------------------------------------------------------------------------
// localStorage-backed report store
// Keyed by child_id — each value is an array of { id, child_id, report_date,
// overall_mood, summary_note, created_by, created_at, updated_at, entries: [] }
// -------------------------------------------------------------------------
function readStore() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // fall through
  }
  const seeded = seedInitial();
  writeStore(seeded);
  return seeded;
}

function writeStore(store) {
  localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

function seedInitial() {
  // Pre-seed yesterday's report for Noa so parent + admin demo views aren't empty.
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const dateStr = `${y}-${m}-${day}`;

  const base = new Date(d);
  base.setHours(9, 15, 0, 0);
  const at = (hour, min) => {
    const copy = new Date(base);
    copy.setHours(hour, min, 0, 0);
    return copy.toISOString();
  };

  const reportId = "demo-report-noa-yesterday";
  const report = {
    id: reportId,
    organization_id: ORG_ID,
    child_id: "00000000-0000-0000-0000-000000000301",
    classroom_id: "00000000-0000-0000-0000-000000000101",
    report_date: dateStr,
    overall_mood: "happy",
    summary_note:
      "Noa had a wonderful day at the sensory table and built a tall tower with the magnet blocks.",
    created_by: "demo-teacher",
    created_at: at(9, 15),
    updated_at: at(15, 45),
    entries: [
      {
        id: "demo-entry-1",
        daily_report_id: reportId,
        entry_type: ENTRY_TYPES.MEAL,
        occurred_at: at(9, 20),
        detail: { meal: "breakfast", amount: "most", items: "oatmeal, banana" },
        created_by: "demo-teacher",
        created_at: at(9, 20),
      },
      {
        id: "demo-entry-2",
        daily_report_id: reportId,
        entry_type: ENTRY_TYPES.ACTIVITY,
        occurred_at: at(10, 30),
        detail: { title: "Sensory table", description: "Water beads + scoops" },
        created_by: "demo-teacher",
        created_at: at(10, 30),
      },
      {
        id: "demo-entry-3",
        daily_report_id: reportId,
        entry_type: ENTRY_TYPES.DIAPER,
        occurred_at: at(11, 10),
        detail: { kind: "wet" },
        created_by: "demo-teacher",
        created_at: at(11, 10),
      },
      {
        id: "demo-entry-4",
        daily_report_id: reportId,
        entry_type: ENTRY_TYPES.MEAL,
        occurred_at: at(12, 0),
        detail: { meal: "lunch", amount: "all", items: "pasta, peas, strawberries" },
        created_by: "demo-teacher",
        created_at: at(12, 0),
      },
      {
        id: "demo-entry-5",
        daily_report_id: reportId,
        entry_type: ENTRY_TYPES.NAP,
        occurred_at: at(13, 0),
        detail: { start_time: "13:00", end_time: "14:30", quality: "sound" },
        created_by: "demo-teacher",
        created_at: at(13, 0),
      },
      {
        id: "demo-entry-6",
        daily_report_id: reportId,
        entry_type: ENTRY_TYPES.ACTIVITY,
        occurred_at: at(15, 0),
        detail: { title: "Outdoor play", description: "Loved the slide today!" },
        created_by: "demo-teacher",
        created_at: at(15, 0),
      },
    ],
  };

  return {
    [report.child_id]: [report],
  };
}

// -------------------------------------------------------------------------
// Public demo API — shape mirrors what dailyReports.js returns in live mode.
// -------------------------------------------------------------------------

export function demoListReportsForChild(childId) {
  const store = readStore();
  return (store[childId] || [])
    .slice()
    .sort((a, b) => b.report_date.localeCompare(a.report_date));
}

export function demoGetReport(childId, dateStr) {
  const list = demoListReportsForChild(childId);
  return list.find((r) => r.report_date === dateStr) || null;
}

export function demoListAllReports({ classroomId, childId, dateFrom, dateTo } = {}) {
  const store = readStore();
  let all = Object.values(store).flat();
  if (classroomId) all = all.filter((r) => r.classroom_id === classroomId);
  if (childId) all = all.filter((r) => r.child_id === childId);
  if (dateFrom) all = all.filter((r) => r.report_date >= dateFrom);
  if (dateTo) all = all.filter((r) => r.report_date <= dateTo);
  return all.sort((a, b) =>
    b.report_date.localeCompare(a.report_date) ||
    b.created_at.localeCompare(a.created_at),
  );
}

export function demoUpsertReport({
  childId,
  classroomId,
  reportDate,
  overallMood,
  summaryNote,
  createdBy,
}) {
  const store = readStore();
  const list = store[childId] || [];
  const existing = list.find((r) => r.report_date === reportDate);
  const now = new Date().toISOString();
  let report;
  if (existing) {
    report = {
      ...existing,
      overall_mood: overallMood ?? existing.overall_mood,
      summary_note: summaryNote ?? existing.summary_note,
      updated_at: now,
    };
    store[childId] = list.map((r) => (r.id === existing.id ? report : r));
  } else {
    report = {
      id: `demo-report-${childId}-${reportDate}`,
      organization_id: ORG_ID,
      child_id: childId,
      classroom_id: classroomId,
      report_date: reportDate,
      overall_mood: overallMood || null,
      summary_note: summaryNote || null,
      created_by: createdBy,
      created_at: now,
      updated_at: now,
      entries: [],
    };
    store[childId] = [...list, report];
  }
  writeStore(store);
  return report;
}

export function demoAddEntry({ report, entryType, detail, createdBy }) {
  const store = readStore();
  const list = store[report.child_id] || [];
  const now = new Date().toISOString();
  const entry = {
    id: `demo-entry-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    daily_report_id: report.id,
    entry_type: entryType,
    occurred_at: now,
    detail: detail || {},
    created_by: createdBy,
    created_at: now,
  };
  const updated = list.map((r) =>
    r.id === report.id
      ? { ...r, entries: [...r.entries, entry], updated_at: now }
      : r,
  );
  store[report.child_id] = updated;
  writeStore(store);
  return entry;
}

export function demoDeleteEntry({ report, entryId }) {
  const store = readStore();
  const list = store[report.child_id] || [];
  const updated = list.map((r) =>
    r.id === report.id
      ? {
          ...r,
          entries: r.entries.filter((e) => e.id !== entryId),
          updated_at: new Date().toISOString(),
        }
      : r,
  );
  store[report.child_id] = updated;
  writeStore(store);
}
